import { c as _c } from "react/compiler-runtime";
import type { ButtonHTMLAttributes, ChangeEventHandler, PropsWithChildren } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Dialog, DialogActions, DialogClose, DialogContent, DialogTrigger } from '@sola/ark-core/molecules/dialog/dialog.tsx';
import type { RadioItemProps } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { useCommitmentPaymentPlanUpdate } from '@sola/my-sdk/commitments/hooks.ts';
import { getRecurringPaymentDay } from '@sola/my-sdk/utils.ts';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.ts';
import type { GraphQLCommitment, GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { userErrorPaymentMethodAssignToCommitment } from '@sola/shared-utils/errors/supporters.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { paymentMethodsQuery } from '@sola/supporter-sdk/queries.ts';
import { LocalAlert } from '../../molecules/local-alerts/local-alerts.tsx';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts.ts';
import type { LocalAlertConfig } from '../../types/localeAlerts.ts';
import { createSupportAlert } from '../../utils/alerts.tsx';
import { getErrorTranslationKeys } from '../../utils/errors.ts';
import { getPaymentMethodLabel } from '../../utils/payment-methods.ts';
import { PaymentMethodAddSheet } from '../payment-method-add-sheet/payment-method-add-sheet.tsx';
enum FormStatusEnum {
  Ok = 'Ok',
  Disabled = 'Disabled',
  Busy = 'Busy',
}
interface FormStatus {
  status: FormStatusEnum;
}
interface FormState extends FormStatus {
  isAddingNewMethod: boolean;
  selectedPaymentMethodId: GraphQLSupporterPaymentMethod['id'];
}
interface PaymentMethodSelectRadioGroupProps {
  selectedValue?: string | null;
  paymentMethods: GraphQLSupporterPaymentMethod[];
  onChange: ChangeEventHandler<HTMLInputElement>;
}
interface PaymentMethodSelectContentProps {
  selectedPaymentMethodId?: GraphQLSupporterPaymentMethod['id'];
  allowAddPaymentMethod?: boolean;
  alerts?: LocalAlertConfig[];
  removeAlert: (id: string) => void;
  // showSelectError: boolean;
  // onSelectErrorClose: () => void;
  onChange: ChangeEventHandler<HTMLInputElement>;
}
interface PaymentMethodSelectDialogProps {
  openTrigger: React.ReactElement;
  commitmentInternalId?: GraphQLCommitment['internalId'];
  currentPaymentMethodId?: GraphQLSupporterPaymentMethod['id'];
  currentPaymentDay?: number | null;
  allowAddPaymentMethod?: boolean;
  onSave: () => Promise<void>;
}
interface PaymentMethodSelectSubmitButtonProps extends PropsWithChildren, ButtonHTMLAttributes<HTMLButtonElement>, FormStatus {}
const addNewMethodValue = 'addMethod';
const getDefaultFormState = (currentPaymentMethodId?: GraphQLSupporterPaymentMethod['id']): FormState => ({
  status: currentPaymentMethodId ? FormStatusEnum.Ok : FormStatusEnum.Disabled,
  isAddingNewMethod: false,
  selectedPaymentMethodId: currentPaymentMethodId ?? ''
});
const useSelectContentData = () => {
  const $ = _c(6);
  const {
    data,
    isLoading,
    isError
  } = useSupporterQuery(paymentMethodsQuery);
  let t0;
  if ($[0] !== data) {
    t0 = data ?? [];
    $[0] = data;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = t0 as GraphQLSupporterPaymentMethod[];
  let t2;
  if ($[2] !== isError || $[3] !== isLoading || $[4] !== t1) {
    t2 = {
      isLoading,
      isError,
      paymentMethods: t1
    };
    $[2] = isError;
    $[3] = isLoading;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const PaymentMethodSelectRadioGroup: React.FC<PaymentMethodSelectRadioGroupProps & Pick<PaymentMethodSelectContentProps, 'allowAddPaymentMethod'>> = t0 => {
  const $ = _c(17);
  const {
    selectedValue,
    allowAddPaymentMethod,
    paymentMethods,
    onChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my", "common"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let T0;
  let t2;
  let t3;
  if ($[1] !== allowAddPaymentMethod || $[2] !== paymentMethods || $[3] !== t) {
    let t4;
    if ($[7] !== t) {
      t4 = paymentMethod => {
        const {
          id: id_0,
          accountNickname
        } = paymentMethod;
        return {
          text: getPaymentMethodLabel(paymentMethod, t),
          value: id_0 as string,
          ...(accountNickname && {
            badge: {
              label: accountNickname,
              size: "sm"
            }
          })
        };
      };
      $[7] = t;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    const radioItems = paymentMethods.filter(_temp).map(t4);
    T0 = RadioGroup;
    if ($[9] !== t) {
      t2 = t("paymentMethods.selectDialog.title");
      $[9] = t;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t3 = allowAddPaymentMethod ? [...radioItems, {
      text: t("paymentMethods.selectDialog.addNewRadioLabel"),
      value: addNewMethodValue
    }] : radioItems;
    $[1] = allowAddPaymentMethod;
    $[2] = paymentMethods;
    $[3] = t;
    $[4] = T0;
    $[5] = t2;
    $[6] = t3;
  } else {
    T0 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[11] !== T0 || $[12] !== onChange || $[13] !== selectedValue || $[14] !== t2 || $[15] !== t3) {
    t4 = <T0 aria-label={t2} items={t3} name="paymentMethod" onChange={onChange} required={true} value={selectedValue} />;
    $[11] = T0;
    $[12] = onChange;
    $[13] = selectedValue;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};
const PaymentMethodSelectContent: React.FC<PaymentMethodSelectContentProps> = t0 => {
  const $ = _c(28);
  const {
    selectedPaymentMethodId,
    allowAddPaymentMethod,
    onChange,
    alerts,
    removeAlert
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    paymentMethods,
    isLoading,
    isError
  } = useSelectContentData();
  if (isLoading) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex flex-col gap-comfy-sm"><Skeleton height={30} width={300} /><Skeleton height={30} width={300} /><Skeleton height={30} width={300} /></div>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (isError) {
    let t2;
    if ($[2] !== t) {
      t2 = t("paymentMethods.queryError.title", {
        count: 0
      });
      $[2] = t;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t) {
      t3 = t("paymentMethods.queryError.message");
      $[4] = t;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t) {
      t4 = t("ctas.contactSupport.url");
      $[6] = t;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== t) {
      t5 = t("ctas.contactSupport.text");
      $[8] = t;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t4 || $[11] !== t5) {
      t6 = <a href={t4}>{t5}</a>;
      $[10] = t4;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== t2 || $[14] !== t3 || $[15] !== t6) {
      t7 = <Alert variant="negative" title={t2} message={t3} action={t6} />;
      $[13] = t2;
      $[14] = t3;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    return t7;
  }
  let t2;
  if ($[17] !== alerts || $[18] !== removeAlert) {
    t2 = alerts?.map(alert => <LocalAlert className="mb-comfy-xl" alert={alert} onClose={removeAlert} />);
    $[17] = alerts;
    $[18] = removeAlert;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  let t3;
  if ($[20] !== allowAddPaymentMethod || $[21] !== onChange || $[22] !== paymentMethods || $[23] !== selectedPaymentMethodId) {
    t3 = <PaymentMethodSelectRadioGroup selectedValue={selectedPaymentMethodId} allowAddPaymentMethod={allowAddPaymentMethod} paymentMethods={paymentMethods} onChange={onChange} />;
    $[20] = allowAddPaymentMethod;
    $[21] = onChange;
    $[22] = paymentMethods;
    $[23] = selectedPaymentMethodId;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  let t4;
  if ($[25] !== t2 || $[26] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  return t4;
};
const useComponentData = ({
  commitmentInternalId,
  currentPaymentMethodId,
  currentPaymentDay,
  onSave
}: {
  commitmentInternalId?: GraphQLCommitment['internalId'];
  currentPaymentMethodId?: GraphQLCommitment['paymentMethodId'];
  currentPaymentDay?: GraphQLCommitment['paymentPullDay'];
  onSave: () => Promise<void>;
}) => {
  const paymentPullDay = currentPaymentDay || getRecurringPaymentDay(new Date().getDate());
  const {
    alerts,
    setAlerts,
    clearAlerts,
    removeAlert
  } = useModalAlerts();
  const paymentPlanUpdate = useCommitmentPaymentPlanUpdate(commitmentInternalId);
  const [formState, setFormState] = useState<FormState>(getDefaultFormState(currentPaymentMethodId));
  const [isOpen, setIsOpen] = useState(false);
  const {
    status,
    isAddingNewMethod,
    selectedPaymentMethodId
  } = formState;
  const handleOpen = (open: boolean) => {
    setFormState(getDefaultFormState(currentPaymentMethodId));
    if (!open) {
      clearAlerts();
    }
    setIsOpen(open);
  };
  const handleChangePaymentMethod: ChangeEventHandler<HTMLInputElement> = e => {
    if (!e.target.value) {
      setFormState(getDefaultFormState(currentPaymentMethodId));
      return;
    }
    if (e.target.value === addNewMethodValue) {
      setFormState({
        status: FormStatusEnum.Ok,
        isAddingNewMethod: true,
        selectedPaymentMethodId: e.target.value
      });
      return;
    }
    setFormState({
      status: FormStatusEnum.Ok,
      isAddingNewMethod: false,
      selectedPaymentMethodId: e.target.value
    });
  };
  const handleSave = async (paymentMethodId?: GraphQLSupporterPaymentMethod['id']) => {
    setFormState(currentState => ({
      ...currentState,
      status: FormStatusEnum.Busy
    }));
    clearAlerts();
    try {
      const {
        success
      } = await paymentPlanUpdate.mutateAsync({
        newPaymentMethodId: paymentMethodId,
        paymentDay: paymentPullDay
      });
      if (!success) {
        throw userErrorPaymentMethodAssignToCommitment();
      }
      if (onSave) {
        try {
          await onSave();
        } catch {
          // Separate try-catch block so a thrown error in onSave doesn't trigger paymentPlanUpdate error
        }
      }
      setIsOpen(false);
    } catch (error) {
      setFormState({
        isAddingNewMethod: false,
        selectedPaymentMethodId: paymentMethodId,
        status: FormStatusEnum.Ok
      });
      const translationKeys = getErrorTranslationKeys(SupporterErrorTypeEnum.PaymentMethodAssignToCommitment, error);
      setAlerts(translationKeys.map(translationKey => {
        return createSupportAlert(translationKey);
      }));
    }
  };
  const handleAdd = async (paymentMethod: GraphQLSupporterPaymentMethod) => {
    if (!paymentMethod) {
      return;
    }
    await handleSave(paymentMethod.id);
  };
  return {
    paymentPullDay,
    alerts,
    removeAlert,
    formState,
    isOpen,
    status,
    isAddingNewMethod,
    selectedPaymentMethodId,
    handleChangePaymentMethod,
    handleOpen,
    handleSave,
    handleAdd
  };
};
const PaymentMethodSelectSubmitButton: React.FC<PaymentMethodSelectSubmitButtonProps> = t0 => {
  const $ = _c(11);
  let buttonProps;
  let children;
  let status;
  if ($[0] !== t0) {
    ({
      children,
      status,
      ...buttonProps
    } = t0);
    $[0] = t0;
    $[1] = buttonProps;
    $[2] = children;
    $[3] = status;
  } else {
    buttonProps = $[1];
    children = $[2];
    status = $[3];
  }
  const t1 = status !== FormStatusEnum.Ok;
  const t2 = status === FormStatusEnum.Busy;
  let t3;
  if ($[4] !== buttonProps || $[5] !== children) {
    t3 = <button type="submit" {...buttonProps}>{children}</button>;
    $[4] = buttonProps;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <ButtonStyle className="w-full md:w-fit" disabled={t1} isBusy={t2}>{t3}</ButtonStyle>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
export const PaymentMethodSelectDialog: React.FC<PaymentMethodSelectDialogProps> = t0 => {
  const $ = _c(50);
  const {
    openTrigger,
    commitmentInternalId,
    currentPaymentMethodId,
    currentPaymentDay,
    allowAddPaymentMethod: t1,
    onSave
  } = t0;
  const allowAddPaymentMethod = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["my", "core"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation(t2);
  let t3;
  if ($[1] !== commitmentInternalId || $[2] !== currentPaymentDay || $[3] !== currentPaymentMethodId || $[4] !== onSave) {
    t3 = {
      commitmentInternalId,
      currentPaymentMethodId,
      currentPaymentDay,
      onSave
    };
    $[1] = commitmentInternalId;
    $[2] = currentPaymentDay;
    $[3] = currentPaymentMethodId;
    $[4] = onSave;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    paymentPullDay,
    alerts,
    removeAlert,
    formState,
    isOpen,
    status,
    isAddingNewMethod,
    selectedPaymentMethodId,
    handleChangePaymentMethod,
    handleOpen,
    handleSave,
    handleAdd
  } = useComponentData(t3);
  let t4;
  if ($[6] !== openTrigger) {
    t4 = <DialogTrigger>{openTrigger}</DialogTrigger>;
    $[6] = openTrigger;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("paymentMethods.selectDialog.title");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t("paymentMethods.selectDialog.supportingText");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== alerts || $[13] !== allowAddPaymentMethod || $[14] !== formState.selectedPaymentMethodId || $[15] !== handleChangePaymentMethod || $[16] !== removeAlert) {
    t7 = <PaymentMethodSelectContent selectedPaymentMethodId={formState.selectedPaymentMethodId} allowAddPaymentMethod={allowAddPaymentMethod} alerts={alerts} removeAlert={removeAlert} onChange={handleChangePaymentMethod} />;
    $[12] = alerts;
    $[13] = allowAddPaymentMethod;
    $[14] = formState.selectedPaymentMethodId;
    $[15] = handleChangePaymentMethod;
    $[16] = removeAlert;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Divider purpose="neutral" className="my-comfy-xl" />;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== paymentPullDay || $[20] !== t) {
    t9 = paymentPullDay && paymentPullDay > 0 && <div className="label-lg label-neutral-mild pb-comfy-sm"><span className="label-potent">{t("commitments.details.paymentDay.label")}{" "}</span>{t("commitments.details.paymentDay.paymentPullDayLabel", {
        count: paymentPullDay,
        ordinal: true
      })}</div>;
    $[19] = paymentPullDay;
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = status === FormStatusEnum.Busy;
  let t11;
  if ($[22] !== t) {
    t11 = t("core:actions.cancel");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <DialogClose>{t11}</DialogClose>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t12) {
    t13 = <ButtonStyle className="w-full md:w-fit" size="md" disabled={t10} purpose="neutral" variant="secondary">{t12}</ButtonStyle>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== handleAdd || $[30] !== handleSave || $[31] !== isAddingNewMethod || $[32] !== selectedPaymentMethodId || $[33] !== status || $[34] !== t) {
    t14 = isAddingNewMethod ? <PaymentMethodAddSheet onComplete={handleAdd} openTrigger={<PaymentMethodSelectSubmitButton status={status}>{t("core:actions.continue")}</PaymentMethodSelectSubmitButton>} /> : <PaymentMethodSelectSubmitButton onClick={async () => await handleSave(selectedPaymentMethodId)} status={status}>{t("core:actions.save")}</PaymentMethodSelectSubmitButton>;
    $[29] = handleAdd;
    $[30] = handleSave;
    $[31] = isAddingNewMethod;
    $[32] = selectedPaymentMethodId;
    $[33] = status;
    $[34] = t;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14) {
    t15 = <DialogActions>{t13}{t14}</DialogActions>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t15 || $[40] !== t5 || $[41] !== t6 || $[42] !== t7 || $[43] !== t9) {
    t16 = <DialogContent icon="info-circle" title={t5} supportingText={t6}>{t7}{t8}{t9}{t15}</DialogContent>;
    $[39] = t15;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t9;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== handleOpen || $[46] !== isOpen || $[47] !== t16 || $[48] !== t4) {
    t17 = <Dialog open={isOpen} onOpenChange={handleOpen}>{t4}{t16}</Dialog>;
    $[45] = handleOpen;
    $[46] = isOpen;
    $[47] = t16;
    $[48] = t4;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  return t17;
};
function _temp(t0) {
  const {
    id
  } = t0;
  return id?.length;
}